import React, { useState, useEffect } from 'react';
import Modal from '../general/Modal';
import Button from '../general/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faFolder, faCardsBlank, faLanguage, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { useMyContentContext } from '../../contexts/MyContentContext';
import { useLanguagesContext } from '../../contexts/LanguagesContext';

const ModalDeckItem = ({ deck, onSelect, isSelected, excludedDeckIds }) => {
    if (excludedDeckIds.includes(deck.id)) return null;
    
    const languageCount = deck.languages ? deck.languages.filter(lang => lang !== null).length : 0;
    
    return (
        <div 
            onClick={() => onSelect(deck)}
            className={`ml-4 hover:bg-gray-200 dark:hover:bg-gray-700 p-3 rounded-lg cursor-pointer flex flex-row justify-start gap-4 place-items-center ${
                isSelected ? 'bg-blue-100 dark:bg-blue-900' : ''
            }`}
        >
            <div>
                <input 
                    readOnly={true} 
                    checked={isSelected} 
                    type="checkbox" 
                />
            </div>
            <div className="text-primary">
                <FontAwesomeIcon icon={faCardsBlank} />
            </div>
            <div className="flex-1">
                <div className="font-medium">{deck.name}</div>
                {languageCount > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <FontAwesomeIcon icon={faLanguage} />
                        {languageCount} language{languageCount !== 1 ? 's' : ''}
                    </div>
                )}
            </div>
        </div>
    );
};

const ModalFolderItem = ({ folder, selectedDeck, onDeckSelect, excludedDeckIds, toggleItems, onToggle, level = 0 }) => {
    const isExpanded = toggleItems[folder.doc_id] || false;
    
    return (
        <div>
            <div 
                onClick={() => onToggle(folder.doc_id)}
                className="hover:bg-gray-200 dark:hover:bg-gray-700 p-3 rounded-lg cursor-pointer flex flex-row justify-start gap-4 place-items-center"
                style={{ marginLeft: `${level * 1}rem` }}
            >
                <div className="text-sandy-brown">
                    <FontAwesomeIcon icon={faFolder} />
                </div>
                <div className="text-gray-400">
                    <FontAwesomeIcon icon={isExpanded ? faCaretDown : faCaretRight} />
                </div>
                <div className="flex-1">
                    {folder.text}
                </div>
            </div>
            {isExpanded && folder.children && (
                <>
                    {folder.children.map((child) => {
                        if (child.content_type === 'folder') {
                            return (
                                <ModalFolderItem
                                    key={child.doc_id}
                                    folder={child}
                                    selectedDeck={selectedDeck}
                                    onDeckSelect={onDeckSelect}
                                    excludedDeckIds={excludedDeckIds}
                                    toggleItems={toggleItems}
                                    onToggle={onToggle}
                                    level={level + 1}
                                />
                            );
                        } else if (child.content_type === 'deck' || child.content_type === 'multilingual-deck') {
                            // Find the actual deck object
                            const deckId = child.id || child.doc_id;
                            const deckObj = child.original_object || { 
                                id: typeof deckId === 'string' ? parseInt(deckId) : deckId, 
                                name: child.text,
                                languages: child.original_object?.languages || []
                            };
                            const isSelected = selectedDeck && selectedDeck.id === deckObj.id;
                            const isExcluded = excludedDeckIds.includes(deckObj.id);
                            
                            if (isExcluded) return null;
                            
                            return (
                                <ModalDeckItem
                                    key={child.doc_id || child.id}
                                    deck={deckObj}
                                    onSelect={onDeckSelect}
                                    isSelected={isSelected}
                                    excludedDeckIds={excludedDeckIds}
                                />
                            );
                        }
                        return null;
                    })}
                </>
            )}
        </div>
    );
};

const ModalContent = ({ closeFunction, onDeckSelect, excludedDeckIds, menuItems, loading }) => {
    const [selectedDeck, setSelectedDeck] = useState(null);
    const [toggleItems, setToggleItems] = useState({});
    const { decks } = useMyContentContext();
    
    useEffect(() => {
        // Initialize toggle state - expand top level by default
        const initialToggle = {};
        if (menuItems && menuItems.length > 0) {
            menuItems.forEach((item) => {
                initialToggle[item.doc_id] = item.content_type === 'folder' ? true : false;
            });
        }
        setToggleItems(initialToggle);
    }, [menuItems]);
    
    const handleToggle = (docId) => {
        setToggleItems(prev => ({
            ...prev,
            [docId]: !prev[docId]
        }));
    };
    
    const handleDeckSelect = (deck) => {
        setSelectedDeck(deck);
    };
    
    const handleConfirm = () => {
        if (selectedDeck) {
            onDeckSelect(selectedDeck);
            closeFunction();
        }
    };
    
    // Create top level folder structure
    const topLevelFolder = {
        text: 'Top level',
        doc_id: 'top_level',
        content_type: 'folder',
        children: menuItems || []
    };
    
    return (
        <div className="overflow-auto min-h-48 w-full sm:h-full">
            <div className="text-lg text-center font-bold mb-4 text-gray-600 dark:text-gray-300">
                Select a deck to add to collection
            </div>
            {!loading ? (
                <>
                    <div className="max-h-96 overflow-y-auto">
                        <ModalFolderItem
                            folder={topLevelFolder}
                            selectedDeck={selectedDeck}
                            onDeckSelect={handleDeckSelect}
                            excludedDeckIds={excludedDeckIds}
                            toggleItems={toggleItems}
                            onToggle={handleToggle}
                        />
                    </div>
                    {selectedDeck && (
                        <div className="mt-4 text-sm text-center text-gray-600 dark:text-gray-400">
                            <span className="italic">Selected:</span> <span className="font-bold">{selectedDeck.name}</span>
                            <div className="flex flex-col-reverse sm:flex-row justify-center gap-1 sm:gap-3 mt-6">
                                <Button 
                                    text="Cancel" 
                                    color="red" 
                                    onClick={closeFunction} 
                                />
                                <Button 
                                    text="Add Deck" 
                                    color="green" 
                                    onClick={handleConfirm}
                                />
                            </div>
                        </div>
                    )}
                    {!selectedDeck && (
                        <div className="flex flex-col-reverse sm:flex-row justify-center gap-1 sm:gap-3 mt-6">
                            <Button 
                                text="Cancel" 
                                color="red" 
                                onClick={closeFunction} 
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center text-4xl">
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                </div>
            )}
        </div>
    );
};

const SelectDeckModal = ({ closeFunction, onDeckSelect, excludedDeckIds, menuItems, loading }) => {
    return (
        <Modal 
            closeFunction={closeFunction} 
            content={
                <ModalContent 
                    closeFunction={closeFunction}
                    onDeckSelect={onDeckSelect}
                    excludedDeckIds={excludedDeckIds || []}
                    menuItems={menuItems}
                    loading={loading}
                />
            } 
            size="3xl"
        />
    );
};

export default SelectDeckModal;
